<template>
  <section
    class="invoice-preview-wrapper"
    aria-label="Détails de la facture fournisseur"
    role="main"
  >
    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="invoice === undefined"
      class="modern-alert d-print-none"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          icon="AlertTriangleIcon"
          class="mr-2"
          size="24"
        />
        <div>
          <h4 class="alert-heading mb-1">
            Facture introuvable
          </h4>
          <div class="alert-body">
            Aucune facture trouvée avec cet ID. Consultez la
            <b-link
              class="alert-link font-weight-bold"
              :to="{ name: 'facture-list' }"
            >
              liste des factures
            </b-link>
            pour voir les autres factures.
          </div>
        </div>
      </div>
    </b-alert>

    <!-- Loading State -->
    <div
      v-if="isLoading === true"
      class="loading-container d-print-none"
      aria-live="polite"
      aria-busy="true"
    >
      <b-spinner
        variant="primary"
        class="loading-spinner"
      />
      <p class="mt-3 text-muted">
        Chargement de la facture...
      </p>
    </div>

    <!-- Main Content -->
    <div v-if="invoice">
      <!-- Top Action Bar -->
      <div class="top-action-bar mb-4 d-print-none">
        <b-row class="align-items-center">
          <b-col
            cols="12"
            md="6"
          >
            <h2 class="page-title mb-0">
              <feather-icon
                icon="FileTextIcon"
                size="24"
                class="mr-2"
              />
              Règlement N° {{ invoice.id }}
            </h2>
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="text-md-right"
          >
            <div class="action-buttons-top">
              <!-- Quick Stats -->
              <div class="quick-stats d-inline-flex mr-2">
                <div class="stat-item-inline success">
                  <feather-icon
                    icon="CheckIcon"
                    size="14"
                  />
                  <span>{{ invoice.nb_delivered }}</span>
                </div>
                <div class="stat-item-inline warning">
                  <feather-icon
                    icon="XIcon"
                    size="14"
                  />
                  <span>{{ invoice.nb_return }}</span>
                </div>
                <div class="stat-item-inline primary">
                  <feather-icon
                    icon="DollarSignIcon"
                    size="14"
                  />
                  <span>{{ invoice.total }} DT</span>
                </div>
              </div>

              <div class="status-indicator mr-2">
                <b-badge :variant="statusBadgeVariant">
                  {{ statusLabel }}
                </b-badge>
                <div class="status-meta">
                  <small
                    v-if="invoice.status === 'confirmed' && invoice.confirmed_at"
                    class="text-muted d-block"
                  >
                    <feather-icon
                      icon="CheckCircleIcon"
                      size="12"
                      class="mr-1"
                    />
                    Confirmée le {{ formatDate(invoice.confirmed_at) }}
                  </small>
                  <small
                    v-else-if="invoice.status === 'cancelled' && invoice.cancelled_at"
                    class="text-muted d-block"
                  >
                    <feather-icon
                      icon="SlashIcon"
                      size="12"
                      class="mr-1"
                    />
                    Annulée le {{ formatDate(invoice.cancelled_at) }}
                  </small>
                </div>
              </div>

              <!-- Payment Status Indicator -->
              <div
                v-if="invoice.status === 'confirmed'"
                class="status-indicator mr-2"
              >
                <b-badge :variant="invoice.is_payment_validated ? 'success' : 'warning'">
                  <feather-icon
                    :icon="invoice.is_payment_validated ? 'CheckCircleIcon' : 'ClockIcon'"
                    size="12"
                    class="mr-1"
                  />
                  {{ invoice.is_payment_validated ? 'Paiement Validé' : 'Paiement En Attente' }}
                </b-badge>
                <div class="status-meta">
                  <small
                    v-if="invoice.is_payment_validated && invoice.payment_validated_at"
                    class="text-muted d-block"
                  >
                    <feather-icon
                      icon="CalendarIcon"
                      size="12"
                      class="mr-1"
                    />
                    Le {{ formatDate(invoice.payment_validated_at) }}
                  </small>
                  <small
                    v-if="invoice.is_payment_validated && paymentValidatorDisplay"
                    class="text-muted d-block font-weight-bold"
                  >
                    <feather-icon
                      icon="UserCheckIcon"
                      size="12"
                      class="mr-1 text-primary"
                    />
                    Confirmé par: {{ paymentValidatorDisplay }}
                  </small>
                </div>
              </div>

              <b-button
                v-if="canValidatePayment"
                variant="success"
                size="sm"
                class="mr-1"
                :disabled="isValidatingPayment"
                @click="handleValidatePayment"
              >
                <b-spinner
                  v-if="isValidatingPayment"
                  small
                  type="border"
                  class="mr-1"
                />
                <feather-icon
                  v-else
                  icon="CheckCircleIcon"
                  size="14"
                  class="mr-1"
                />
                Valider Paiement
              </b-button>

              <b-button
                v-if="canConfirm"
                variant="success"
                size="sm"
                class="mr-1"
                :disabled="isConfirming"
                @click="handleConfirm"
              >
                <feather-icon
                  icon="CheckIcon"
                  size="14"
                  class="mr-1"
                />
                Confirmer
              </b-button>

              <b-button
                v-if="canCancel"
                variant="danger"
                size="sm"
                class="mr-1"
                :disabled="isCancelling"
                @click="handleCancel"
              >
                <feather-icon
                  icon="SlashIcon"
                  size="14"
                  class="mr-1"
                />
                Annuler
              </b-button>

              <b-dropdown
                v-ripple.400="'rgba(10, 38, 52, 0.15)'"
                variant="outline-primary"
                class="action-btn-top ml-2"
                :disabled="isLoading"
                text="Imprimer"
                right
              >
                <template #button-content>
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Imprimer
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="14"
                    class="ml-1"
                  />
                </template>

                <b-dropdown-item
                  v-b-tooltip.hover.right="'Imprimer une version simplifiée en noir et blanc, sans la colonne Motifs de retour.'"
                  @click="printInvoiceBW"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Version noir et blanc
                </b-dropdown-item>

                <b-dropdown-item
                  v-b-tooltip.hover.right="'Imprimer la facture en couleur avec tous les éléments graphiques.'"
                  @click="printInvoice"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Version couleur
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Invoice Content - This is what gets printed -->
      <div
        id="invoice-content"
        class="invoice-content"
      >
        <!-- Full Width Invoice Document -->
        <b-card class="invoice-card shadow-sm">
          <!-- Header Section -->
          <div class="invoice-header">
            <b-row class="align-items-center">
              <b-col
                cols="12"
                md="6"
                class="mb-3 mb-md-0"
              >
                <div class="company-logo">
                  <img
                    src="@/assets/images/logo/team-parcel-logo.png"
                    :alt="$branding.logo.alt"
                    class="logo-img"
                  >
                </div>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="text-md-right"
              >
                <h1 class="invoice-title">
                  Règlement N°
                  <span class="invoice-number">{{ invoice.id }}</span>
                </h1>
                <div class="invoice-date">
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="mr-1"
                  />
                  {{ formatFn(invoice.created_at) }}
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Company Information -->
          <b-row class="company-info-section">
            <b-col
              cols="12"
              md="6"
            >
              <document-company-info
                :company="company"
                header-title="Notre Société"
              >
                <template #icon>
                  <feather-icon
                    icon="BuildingIcon"
                    size="20"
                    class="mr-2"
                  />
                </template>
              </document-company-info>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <div
                v-if="invoice.supplier"
                class="info-card supplier-card"
              >
                <div class="info-header">
                  <feather-icon
                    icon="UserIcon"
                    size="20"
                    class="mr-2"
                  />
                  <h5 class="mb-0">
                    Fournisseur
                  </h5>
                </div>
                <div class="info-content">
                  <div class="info-item">
                    <strong>{{ invoice.supplier.username }}</strong>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Matricule fiscale:</span>
                    <span>{{ invoice.supplier.tax_identification_number }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Adresse:</span>
                    <span>{{ invoice.supplier.address }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Téléphone:</span>
                    <span>{{ invoice.supplier.phone }}</span>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Summary Table -->
          <div class="summary-section">
            <h5 class="section-title">
              <feather-icon
                icon="BarChartIcon"
                size="20"
                class="mr-2"
              />
              Résumé de la facture
            </h5>
            <div class="summary-table-container">
              <div class="table-responsive">
                <table class="modern-table">
                  <thead>
                    <tr>
                      <th>Nombre de colis</th>
                      <th>Colis livré</th>
                      <th>Colis retour</th>
                      <th>Total COD</th>
                      <th>Frais de livraison</th>
                      <th>Frais</th>
                      <th>Net à payer</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <b-badge
                          variant="info"
                          class="stat-badge"
                        >
                          {{ invoice.total_nb }}
                        </b-badge>
                      </td>
                      <td>
                        <b-badge
                          variant="success"
                          class="stat-badge"
                        >
                          {{ invoice.nb_delivered }}
                        </b-badge>
                      </td>
                      <td>
                        <b-badge
                          variant="warning"
                          class="stat-badge"
                        >
                          {{ invoice.nb_return }}
                        </b-badge>
                      </td>
                      <td class="amount">
                        {{ invoice.total_ttc }} DT
                      </td>
                      <td class="amount">
                        {{ invoice.total_delivery }} DT
                      </td>
                      <td class="amount highlight-fee">
                        <div
                          v-if="settlementFeeRows.length"
                          class="summary-fee-breakdown"
                        >
                          <div
                            v-for="line in settlementFeeRows"
                            :key="line.key"
                            class="summary-fee-line"
                          >
                            <span>{{ line.name }}</span>
                            <strong>{{ formatMoney(line.amount) }} DT</strong>
                          </div>
                        </div>
                        <span v-else>{{ formatMoney(invoice.frais_societe_livraison || 0) }} DT</span>
                      </td>
                      <td class="amount total-amount">
                        {{ invoice.net_a_payer || invoice.total }} DT
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Cancellation Reason -->
          <b-alert
            v-if="invoice.status === 'cancelled' && invoice.cancellation_reason"
            variant="danger"
            show
            class="mt-2"
          >
            <feather-icon
              icon="AlertTriangleIcon"
              class="mr-1"
            />
            Motif d'annulation : {{ invoice.cancellation_reason }}
          </b-alert>

          <!-- Delivered Orders -->
          <div
            v-if="delivredOrder.length"
            class="orders-section"
          >
            <div class="section-header">
              <h5 class="section-title">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="20"
                  class="mr-2 text-success"
                />
                Colis livrés
                <b-badge
                  variant="success"
                  class="ml-2"
                >
                  {{ delivredOrder.length }}
                </b-badge>
              </h5>
            </div>
            <div class="table-container">
              <b-table-lite
                responsive
                class="modern-orders-table"
                :items="delivredOrder"
                :fields="[
                  { key: 'code', label: 'BL' },
                  { key: 'destinataire', label: 'Destinataire' },
                  { key: 'gouvernorat', label: 'Gouvernorat' },
                  { key: 'extra', label: 'Extra' },
                  { key: 'prix', label: 'Prix' }
                ]"
                striped
                hover
              >
                <template #thead-top>
                  <tr class="print-table-logo-row">
                    <th
                      :colspan="5"
                      class="print-table-logo-cell"
                    >
                      <img
                        src="@/assets/images/logo/team-parcel-logo.png"
                        :alt="$branding.logo.alt"
                        class="print-table-logo"
                      >
                    </th>
                  </tr>
                </template>
                <template #cell(code)="data">
                  <div class="order-code">
                    <strong>{{ data.item.code }}</strong>
                    <b-badge
                      v-if="data.item.is_an_exchange"
                      variant="primary"
                      class="ml-2"
                    >
                      Échange
                    </b-badge>
                  </div>
                </template>
                <template #cell(prix)="data">
                  <span class="amount">{{ data.item.prix }} DT</span>
                </template>

              </b-table-lite>
            </div>
          </div>

          <!-- Return Orders -->
          <div
            v-if="retour && retour.length"
            class="orders-section d-print-none"
          >
            <div class="section-header">
              <h5 class="section-title">
                <feather-icon
                  icon="XCircleIcon"
                  size="20"
                  class="mr-2 text-danger"
                />
                Colis retournés
                <b-badge
                  variant="danger"
                  class="ml-2"
                >
                  {{ retour.length }}
                </b-badge>
              </h5>
            </div>
            <div class="table-container">
              <b-table-lite
                responsive
                class="modern-orders-table"
                :items="retour"
                :fields="[
                  { key: 'code', label: 'BL' },
                  { key: 'destinataire', label: 'Destinataire' },
                  { key: 'gouvernorat', label: 'Gouvernorat' },
                  { key: 'extra', label: 'Extra' },
                  { key: 'motifs_retour', label: 'Motifs de retour', thClass: 'motifs-column', tdClass: 'motifs-column' }
                ]"
                striped
                hover
              >
                <template #thead-top>
                  <tr class="print-table-logo-row">
                    <th
                      :colspan="5"
                      class="print-table-logo-cell"
                    >
                      <img
                        src="@/assets/images/logo/team-parcel-logo.png"
                        :alt="$branding.logo.alt"
                        class="print-table-logo"
                      >
                    </th>
                  </tr>
                </template>
                <template #cell(code)="data">
                  <div class="order-code">
                    <strong>{{ data.item.code }}</strong>
                    <b-badge
                      v-if="data.item.is_an_exchange"
                      variant="primary"
                      class="ml-2"
                    >
                      Échange
                    </b-badge>
                  </div>
                </template>
                <template #cell(gouvernorat)="data">
                  <span>{{ data.item.gouvernorat && data.item.gouvernorat.name ? data.item.gouvernorat.name : data.item.gouvernorat }}</span>
                </template>

                <template #cell(motifs_retour)="data">
                  <div
                    v-if="data.item.return_reasons && data.item.return_reasons.length"
                    class="return-reasons"
                  >
                    <div
                      v-for="(reason, idx) in data.item.return_reasons"
                      :key="idx"
                      class="reason-item"
                    >
                      <div class="reason-main">
                        <div class="reason-text">
                          <strong class="reason-title">{{ reason.return_reason }}</strong>
                          <div
                            v-if="reason.additional_note"
                            class="reason-note"
                          >
                            <feather-icon
                              icon="MessageSquareIcon"
                              size="12"
                              class="mr-1"
                            />
                            {{ reason.additional_note }}
                          </div>
                        </div>

                        <div class="reason-meta">
                          <div
                            v-if="reason.created_at"
                            class="reason-date"
                          >
                            <feather-icon
                              icon="ClockIcon"
                              size="12"
                              class="mr-1"
                            />
                            <span class="date-label">Signalé:</span>
                            <span class="date-value">{{ formatDateOnly(reason.created_at) }}</span>
                          </div>
                          <div
                            v-if="reason.scheduled_date"
                            class="reason-date scheduled"
                          >
                            <feather-icon
                              icon="CalendarIcon"
                              size="12"
                              class="mr-1"
                            />
                            <span class="date-label">Planifié:</span>
                            <span class="date-value">{{ formatDateOnly(reason.scheduled_date) }}</span>
                          </div>
                        </div>

                        <div
                          v-if="reason.quick_options && reason.quick_options.length"
                          class="reason-options"
                        >
                          <div class="options-label">
                            Options:
                          </div>
                          <div class="options-list">
                            <b-badge
                              v-for="(option, optionIdx) in reason.quick_options"
                              :key="optionIdx"
                              variant="outline-secondary"
                              class="option-badge"
                            >
                              {{ option }}
                            </b-badge>
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="idx < data.item.return_reasons.length - 1"
                        class="reason-divider"
                      />
                    </div>
                  </div>
                  <div
                    v-else
                    class="no-reason"
                  >
                    <feather-icon
                      icon="MinusIcon"
                      size="14"
                      class="mr-1 text-muted"
                    />
                    <span class="text-muted">Aucun motif spécifié</span>
                  </div>
                </template>
              </b-table-lite>
            </div>
          </div>

          <!-- Signature Section -->
          <document-signature
            left-label="Signature du fournisseur"
            :company="company"
          />
        </b-card>
      </div>
    </div>
  </section>
</template>

<script>
import { ref } from '@vue/composition-api'
import {
  BRow,
  BCol,
  BCard,
  BTableLite,
  BAlert,
  BLink,
  BBadge,
  BSpinner,
  BDropdown,
  BDropdownItem,
  BButton,
} from 'bootstrap-vue'

import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { DocumentCompanyInfo, DocumentSignature } from '@/components/document'

export default {
  directives: {
    Ripple,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BTableLite,
    BAlert,
    BLink,
    BBadge,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BButton,
    DocumentCompanyInfo,
    DocumentSignature,
  },
  data() {
    return {
      fields: [
        {
          key: 'customer',
          label: 'Client',
          formatter: customer => `${customer.company_name}`,
        },
        {
          key: 'customer.address',
          label: 'address',
        },
        {
          key: 'extra_fee_value',
          label: 'Extra',
        },
        {
          key: 'customer.tax_identification_number',
          label: 'MF',
        },
      ],
      p: [],
      test: '0',
      invoice: null,
      delivredOrder: [],
      retour: [],
      company: {},
      isLoading: false,
      isConfirming: false,
      isCancelling: false,
      isValidatingPayment: false,
      user: {},
    }
  },
  computed: {
    statusLabel() {
      if (!this.invoice) return ''
      return this.formatStatus(this.invoice.status)
    },
    statusBadgeVariant() {
      if (!this.invoice) return 'secondary'
      switch (this.invoice.status) {
        case 'confirmed':
          return 'success'
        case 'cancelled':
          return 'danger'
        case 'draft':
        default:
          return 'secondary'
      }
    },
    canConfirm() {
      return this.user.role === 'admin' && this.invoice && this.invoice.status === 'draft'
    },
    canCancel() {
      return this.user.role === 'admin' && this.invoice && this.invoice.status === 'confirmed' && this.invoice.can_cancel
    },
    canValidatePayment() {
      if (!this.invoice || this.invoice.status !== 'confirmed' || this.invoice.is_payment_validated) return false
      const allowedRoles = ['admin', 'superadmin', 'accountant', 'supplier']
      const userRole = (this.user && this.user.role) || ''
      if (allowedRoles.includes(userRole)) return true
      if (this.user && this.user.groups && Array.isArray(this.user.groups)) {
        return this.user.groups.some(g => allowedRoles.includes(typeof g === 'string' ? g : g.name))
      }
      return false
    },
    paymentValidatorDisplay() {
      if (!this.invoice || !this.invoice.is_payment_validated) return null
      const validatorObj = this.invoice.payment_validated_by
      let name = null
      let roleKey = null
      if (typeof validatorObj === 'object' && validatorObj !== null) {
        name = validatorObj.page_name || validatorObj.username
        roleKey = validatorObj.role
      } else {
        name = this.invoice.payment_validated_by_name || this.invoice.payment_validated_by
        roleKey = this.invoice.payment_validated_by_role
      }
      if (!name) return null
      const roleMap = {
        admin: 'Admin',
        superadmin: 'Superadmin',
        accountant: 'Comptable',
        supplier: 'Fournisseur',
        manager: 'Gestionnaire',
      }
      const roleLabel = roleMap[(roleKey || '').toLowerCase()] || roleKey
      return roleLabel ? `${name} (${roleLabel})` : name
    },
    settlementFeeRows() {
      if (!this.invoice) return []
      const breakdown = this.invoice.fee_breakdown || []

      if (breakdown.length) {
        return breakdown.map((line, index) => ({
          key: line.id || line.configuration_id || `${line.name}-${index}`,
          name: line.name || 'Frais société',
          amount: Math.abs(Number(line.calculated_amount ?? line.signed_amount ?? 0)),
        }))
      }

      const aggregateFee = Number(this.invoice.frais_societe_livraison || 0)
      if (!aggregateFee) return []

      return [{
        key: 'frais-societe',
        name: 'Frais société',
        amount: Math.abs(aggregateFee),
      }]
    },
  },
  created() {
    this.id = this.$route.params.id
    this.company = storeAuth.state.company
    this.user = storeAuth.state.user
    this.getInvoice()
  },
  methods: {
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    formatDateOnly(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      return `${day1.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    },
    formatStatus(status) {
      if (!status) return 'Inconnu'
      const map = {
        draft: 'Brouillon',
        confirmed: 'Confirmée',
        cancelled: 'Annulée',
      }
      return map[status] || status
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(3)
    },
    async handleValidatePayment() {
      const isSupplier = this.user && this.user.role === 'supplier'
      const promptNote = isSupplier
        ? 'Confirmez uniquement si vous avez bien reçu le paiement. Cette validation est définitive.'
        : "Confirmez-vous la validation du paiement pour cette facture fournisseur au nom de l'entreprise ?"

      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `Facture #${this.invoice.id} - Total Net: ${this.formatMoney(this.invoice.net_a_payer || this.invoice.total)} TND\n\n${promptNote}`,
          {
            title: isSupplier ? 'Valider le paiement reçu' : 'Valider le paiement de la facture',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'success',
            okTitle: 'Oui, valider',
            cancelTitle: 'Annuler',
            footerClass: 'p-2',
            centered: true,
          },
        )

        if (confirmed) {
          this.isValidatingPayment = true
          const endpoint = isSupplier
            ? 'api/invoices/validate-payment/'
            : 'api/invoices/admin-validate-payment/'

          const response = await axios.patch(endpoint, {
            invoice_id: this.invoice.id,
          })
          if (response.data && response.data.success) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Paiement validé',
                icon: 'CheckCircleIcon',
                variant: 'success',
                text: response.data.message || 'Paiement de facture validé avec succès.',
              },
            })
            await this.getInvoice()
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Erreur',
                icon: 'AlertCircleIcon',
                variant: 'danger',
                text: (response.data && response.data.message) || 'Erreur lors de la validation.',
              },
            })
          }
        }
      } catch (error) {
        let msg = 'Erreur lors de la validation du paiement.'
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: msg,
          },
        })
      } finally {
        this.isValidatingPayment = false
      }
    },
    async getInvoice() {
      this.isLoading = true
      this.delivredOrder = []
      this.retour = []
      try {
        const { data } = await axios.get(`/api/invoices/detail/${this.$route.params.id}`)
        this.invoice = data
        const { delivred_orders: delivredOrders, return_orders: returnOrders } = data

        delivredOrders.forEach(item => {
          const dataObj = {
            code: item.display_cross_tenant_barcode && item.cross_tenant_barcode ? item.cross_tenant_barcode : item.id,
            gouvernorat: item.governorate,
            extra: item.extra_fee_value || '-',
            destinataire: item.name,
            adresse: item.address,
            prix: item.price_ttc,
            prixLivraison: item.price_delivery,
            status: 'livré',
            is_an_exchange: item.is_an_exchange,
          }
          this.delivredOrder.push(dataObj)
        })

        returnOrders.forEach(item => {
          const dataObj = {
            code: item.display_cross_tenant_barcode && item.cross_tenant_barcode ? item.cross_tenant_barcode : item.id,
            gouvernorat: item.governorate, // now a string (governorate.name)
            extra: item.extra_fee_value || '-',
            destinataire: item.name,
            adresse: item.address,
            prix: item.price_ttc,
            prixLivraison: item.price_delivery,
            prixRetour: item.price_return,
            status: 'retour',
            is_an_exchange: item.is_an_exchange,
            return_reasons: item.return_reasons || [],
          }
          this.retour.push(dataObj)
        })
      } finally {
        this.isLoading = false
      }
    },
    async refreshInvoice() {
      await this.getInvoice()
    },
    async handleConfirm() {
      this.isConfirming = true
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `Confirmer la facture #${this.invoice.id} ?\n\nCette action marquera les commandes comme payées.`,
          {
            title: 'Confirmer la facture',
            size: 'sm',
            okVariant: 'success',
            okTitle: 'Confirmer',
            cancelTitle: 'Annuler',
            footerClass: 'p-2',
            centered: true,
          },
        )

        if (!confirmed) {
          return
        }

        await axios.post('/api/invoices/confirm/', { invoice_id: this.invoice.id })
        this.showToast('success', 'Facture confirmée avec succès')
        await this.refreshInvoice()
      } catch (error) {
        const message = error.response?.data?.error || 'Impossible de confirmer la facture'
        this.showToast('danger', message)
      } finally {
        this.isConfirming = false
      }
    },
    async handleCancel() {
      this.isCancelling = true
      try {
        const reason = await this.$bvModal.msgBoxPrompt(
          `Vous êtes sur le point d'annuler la facture confirmée #${this.invoice.id}.\n\nCette opération est autorisée uniquement le jour de la confirmation et rétablira les statuts des commandes.\n\nMotif d'annulation (optionnel):`,
          {
            title: 'Annuler la facture',
            size: 'sm',
            okVariant: 'danger',
            okTitle: 'Annuler la facture',
            cancelTitle: 'Retour',
            footerClass: 'p-2',
            centered: true,
            hideHeaderClose: false,
            inputAttrs: {
              maxlength: 255,
            },
          },
        )

        if (reason === null) {
          return
        }

        await axios.post('/api/invoices/cancel/', {
          invoice_id: this.invoice.id,
          reason: reason || '',
        })
        this.showToast('success', 'Facture annulée avec succès')
        await this.refreshInvoice()
      } catch (error) {
        const message = error.response?.data?.error || 'Impossible d\'annuler la facture'
        this.showToast('danger', message)
      } finally {
        this.isCancelling = false
      }
    },
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant,
          title: 'Notification',
          text,
        },
      }, { position: 'top-right' })
    },
  },
  setup() {
    const invoiceData = ref(null)
    const paymentDetails = ref({})
    const sommeFrais = 0
    const total = 0
    const tax = 0
    const sommeHT = 0
    const printInvoice = () => {
      const originalTitle = document.title
      const faviconEls = Array.from(document.querySelectorAll("link[rel~='icon'], link[rel='shortcut icon']"))
      const originalFavicons = faviconEls.map(el => ({ el, href: el.getAttribute('href') }))
      faviconEls.forEach(el => {
        el.setAttribute('href', "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'></svg>")
      })
      document.title = ' '

      const keepBrowserHeaderFooterBlankInterval = window.setInterval(() => {
        document.title = ' '
        faviconEls.forEach(el => {
          el.setAttribute('href', "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'></svg>")
        })
      }, 100)

      // Add print styles to the document temporarily (same approach as pickup page)
      const printCSS = `
        <style id="print-styles">
          @page {
            margin: 5mm;
          }
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              font-family: Arial, sans-serif;
              width: 100% !important;
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
            }
            body * { visibility: hidden; }
            .motifs-column { display: none !important; }
            .top-action-bar,
            .loading-container,
            .modern-alert,
            nav.header-navbar,
            .main-menu,
            .header-navbar-shadow,
            footer.footer,
            .customizer-toggle {
              display: none !important;
            }
            #app,
            .app-content,
            .content.app-content,
            .app-content .content-wrapper,
            .app-content .content-body,
            .content-area-wrapper,
            .invoice-preview-wrapper {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              min-height: 0 !important;
              height: auto !important;
              background: white !important;
              overflow: visible !important;
            }
            #invoice-content, #invoice-content * { visibility: visible; }
            #invoice-content { 
              position: static; 
              left: 0; 
              top: 0; 
              width: 100%;
              max-width: 100%;
              margin: 0 !important;
              padding: 0 !important;
              box-sizing: border-box;
              overflow: visible;
              font-family: Arial, sans-serif;
              font-size: 16px;
              line-height: 1.5;
              color: black;
              page-break-after: auto;
              break-after: auto;
            }
            #invoice-content *, #invoice-content *::before, #invoice-content *::after {
              box-sizing: border-box;
            }
            #invoice-content .invoice-card,
            #invoice-content .card,
            #invoice-content .card-body {
              margin: 0 !important;
              box-shadow: none !important;
              border-radius: 0 !important;
              overflow: visible !important;
              page-break-after: auto;
              break-after: auto;
            }
            .d-print-none, .feather-icon, .no-print { display: none !important; }
            .invoice-header { 
              margin-bottom: 10px; 
              padding: 14px;
              background: rgba(17, 64, 146, 0.12) !important;
              color: black !important;
              border: 2px solid black;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              page-break-inside: avoid;
            }
            .invoice-header .mb-3 {
              margin-bottom: 6px !important;
            }
            .invoice-header .company-logo {
              margin-bottom: 4px;
            }
            .invoice-header .company-logo .logo-img {
              display: none !important;
            }
            .invoice-header .col-md-6 { flex: 0 0 100% !important; max-width: 100% !important; }
            .invoice-title { 
              font-size: 24px; 
              font-weight: bold; 
              color: black !important;
              margin: 0 0 4px 0;
            }
            .invoice-number {
              color: #114092 !important;
              font-size: 14px;
            }
            .invoice-date {
              font-size: 14px;
              color: black !important;
            }
            .section-title { 
              font-size: 16px;
              font-weight: bold; 
              border-bottom: 1px solid black; 
              margin: 12px 0 10px 0; 
              padding-bottom: 4px;
              color: black !important;
              page-break-after: avoid;
            }
            .row { display: flex; flex-wrap: wrap; margin: 0; width: 100%; max-width: 100%; }
            .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 3px; box-sizing: border-box; }
            .info-card { 
              border: 1px solid black; 
              padding: 10px; 
              margin-bottom: 10px; 
              background: white !important;
              border-left: 4px solid #0A2634 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              min-height: 100px;
              page-break-inside: avoid;
            }
            .supplier-card {
              border-left-color: #2D3748 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              min-height: 100px;
            }
            .info-header {
              font-size: 14px;
              font-weight: bold;
              color: black !important;
              margin-bottom: 8px;
              border-bottom: 1px solid black;
              padding-bottom: 3px;
            }
            .info-item {
              margin-bottom: 6px;
              font-size: 13px;
              color: black !important;
            }
            .info-label {
              font-weight: bold;
              color: black !important;
              min-width: 80px;
              display: inline-block;
            }
            .info-subtitle {
              font-style: italic;
              color: black !important;
              font-size: 12px;
            }
            .summary-table-container {
              border: 1px solid black;
              margin-bottom: 15px;
              background: white !important;
              page-break-inside: avoid;
            }
            .table-container {
              border: 1px solid black;
              margin-bottom: 15px;
              background: white !important;
              /* Allow tables to break across pages */
            }
            .table-container, .summary-table-container {
              max-width: 100%;
              overflow: hidden;
            }
            .modern-table, .modern-orders-table {
              width: 100%;
              max-width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              font-size: 14px;
              word-wrap: break-word;
              overflow-wrap: break-word;
              page-break-inside: auto;
            }
            .modern-table thead, .modern-orders-table thead {
              display: table-header-group;
              background: transparent !important;
            }
            .modern-table th {
              background: transparent !important;
              color: black !important;
              padding: 8px 5px;
              border: none !important;
              border-bottom: 2px solid black !important;
              font-weight: bold;
              font-size: 14px;
              text-align: center;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .modern-table td, .modern-orders-table td {
              padding: 6px 4px;
              border: none !important;
              color: black !important;
              font-size: 13px;
              text-align: center;
              page-break-inside: avoid;
              page-break-after: auto;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .modern-orders-table th {
              background: transparent !important;
              color: black !important;
              padding: 8px 5px;
              border: none !important;
              border-bottom: 2px solid black !important;
              font-weight: bold;
              font-size: 14px;
              text-align: left;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .modern-orders-table tbody tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            .print-table-logo-row {
              display: none !important;
            }
            .print-table-logo-row th {
              background: transparent !important;
              color: black !important;
              border: none !important;
              padding: 4px 0 6px;
            }
            .print-table-logo {
              display: none !important;
            }
            .stat-badge {
              background: none !important;
              color: black !important;
              border: 1px solid black !important;
              padding: 2px 6px;
              font-size: 13px;
              font-weight: bold;
            }
            .amount {
              font-weight: bold;
              color: black !important;
              font-size: 14px;
            }
            .total-amount {
              font-weight: bold;
              color: #0A2634 !important;
              font-size: 15px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .return-reasons {
              max-width: none;
            }
            .reason-main {
              background: #f9f9f9 !important;
              border-left: 3px solid #0A2634 !important;
              padding: 5px;
              margin-bottom: 5px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              page-break-inside: avoid;
            }
            .reason-title {
              color: #0A2634 !important;
              font-weight: bold;
              font-size: 14px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .reason-meta, .reason-note, .reason-options {
              font-size: 13px;
              color: black !important;
            }
            .option-badge {
              background: none !important;
              border: 1px solid #ccc !important;
              color: black !important;
              padding: 2px 5px;
              font-size: 12px;
            }
            .signature-section {
              margin-top: 16px;
              padding-top: 8px;
              page-break-inside: avoid;
              page-break-after: auto;
              break-after: auto;
            }
            .signature-title {
              font-weight: bold;
              color: black !important;
              margin-bottom: 12px;
              font-size: 13px;
              text-align: center;
            }
            .signature-line {
              height: 50px;
              border-bottom: 1px solid black;
              margin-bottom: 3px;
              background: white !important;
            }
            .ml-2, .badge:not(.stat-badge) {
              display: none !important;
            }
            .orders-section {
              margin-bottom: 15px;
              /* Allow section to break across pages */
            }
            .section-header {
              page-break-after: avoid;
            }
            .company-info-section {
              page-break-inside: avoid;
            }
            .summary-section {
              page-break-inside: avoid;
            }
            * {
              transition: none !important;
              animation: none !important;
              transform: none !important;
            }
          }
        </style>
      `

      // Add the print styles to the document head
      document.head.insertAdjacentHTML('beforeend', printCSS)

      const cleanup = () => {
        window.clearInterval(keepBrowserHeaderFooterBlankInterval)
        const printStyleElement = document.getElementById('print-styles')
        if (printStyleElement) {
          printStyleElement.remove()
        }

        document.title = originalTitle
        originalFavicons.forEach(({ el, href }) => {
          if (href) {
            el.setAttribute('href', href)
          }
        })
      }

      window.addEventListener('afterprint', cleanup, { once: true })

      setTimeout(() => {
        window.print()
      }, 150)

      setTimeout(() => {
        cleanup()
      }, 2000)
    }

    const printInvoiceBW = () => {
      // Black & White optimized print styles
      const printCSS = `
        <style id="print-styles-bw">
          @page {
            margin: 5mm;
          }
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              font-family: Arial, sans-serif;
              width: 100% !important;
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
            }
            body * { visibility: hidden; }
            .top-action-bar,
            .loading-container,
            .modern-alert,
            nav.header-navbar,
            .main-menu,
            .header-navbar-shadow,
            footer.footer,
            .customizer-toggle {
              display: none !important;
            }
            #app,
            .app-content,
            .content.app-content,
            .app-content .content-wrapper,
            .app-content .content-body,
            .content-area-wrapper,
            .invoice-preview-wrapper {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              min-height: 0 !important;
              height: auto !important;
              background: white !important;
              overflow: visible !important;
            }
            #invoice-content, #invoice-content * { visibility: visible; }
            #invoice-content { 
              position: static; 
              left: 0; 
              top: 0; 
              width: 100%;
              max-width: 100%;
              margin: 0 !important;
              padding: 0 !important;
              box-sizing: border-box;
              overflow: visible;
              font-family: Arial, sans-serif;
              font-size: 14px;
              line-height: 1.4;
              color: black;
              page-break-after: auto;
              break-after: auto;
            }
            #invoice-content *, #invoice-content *::before, #invoice-content *::after {
              box-sizing: border-box;
            }
            #invoice-content .invoice-card,
            #invoice-content .card,
            #invoice-content .card-body {
              margin: 0 !important;
              box-shadow: none !important;
              border-radius: 0 !important;
              overflow: visible !important;
              page-break-after: auto;
              break-after: auto;
            }
            .d-print-none, .feather-icon, .no-print { display: none !important; }
            
            /* Hide motifs de retour column in B&W mode */
            .motifs-column { display: none !important; }
            
            .invoice-header { 
              margin-bottom: 10px; 
              padding: 14px;
              background: white !important;
              color: black !important;
              border: 2px solid black;
              page-break-inside: avoid;
            }
            .invoice-header .mb-3 {
              margin-bottom: 6px !important;
            }
            .invoice-header .company-logo {
              margin-bottom: 4px;
            }
            .logo-img { display: none !important; }
            .invoice-header .company-logo .logo-img {
              display: none !important;
            }
            .invoice-header .col-md-6 { flex: 0 0 100% !important; max-width: 100% !important; }
            .invoice-title { 
              font-size: 24px; 
              font-weight: bold; 
              color: black !important;
              margin: 0 0 4px 0;
            }
            .invoice-number, .invoice-date {
              color: black !important;
              font-size: 14px;
            }
            .section-title { 
              font-size: 16px; 
              font-weight: bold; 
              border-bottom: 1px solid black; 
              margin: 12px 0 10px 0; 
              padding-bottom: 4px;
              color: black !important;
              page-break-after: avoid;
            }
            .text-success, .text-danger, .text-warning, .text-info, .text-primary {
              color: black !important;
            }
            .row { display: flex; flex-wrap: wrap; margin: 0; width: 100%; max-width: 100%; }
            .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 3px; box-sizing: border-box; }
            .info-card { 
              border: 1px solid black; 
              padding: 10px; 
              margin-bottom: 10px; 
              background: white !important;
              min-height: 100px;
              page-break-inside: avoid;
            }
            .info-header {
              font-size: 14px;
              font-weight: bold;
              color: black !important;
              margin-bottom: 8px;
              border-bottom: 1px solid black;
              padding-bottom: 3px;
            }
            .info-item {
              margin-bottom: 6px;
              font-size: 13px;
              color: black !important;
            }
            .info-label {
              font-weight: bold;
              color: black !important;
              min-width: 80px;
              display: inline-block;
            }
            .info-subtitle {
              font-style: italic;
              color: black !important;
              font-size: 12px;
            }
            .summary-table-container {
              border: 1px solid black;
              margin-bottom: 15px;
              background: white !important;
              page-break-inside: avoid;
            }
            .table-container {
              border: 1px solid black;
              margin-bottom: 15px;
              background: white !important;
              /* Allow tables to break across pages */
            }
            .table-container, .summary-table-container {
              max-width: 100%;
              overflow: hidden;
            }
            .modern-table, .modern-orders-table {
              width: 100%;
              max-width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              font-size: 14px;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            .modern-orders-table thead {
              display: table-header-group;
            }
            .modern-table th, .modern-orders-table th {
              background: transparent !important;
              color: black !important;
              padding: 8px 5px;
              border: none !important;
              border-bottom: 2px solid black !important;
              font-weight: bold;
              font-size: 14px;
              text-align: center;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .modern-orders-table th {
              text-align: left;
            }
            .modern-table td, .modern-orders-table td {
              padding: 6px 4px;
              border: none !important;
              color: black !important;
              font-size: 13px;
              text-align: center;
              page-break-inside: avoid;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .modern-orders-table tbody tr {
              page-break-inside: avoid;
            }
            .print-table-logo-row {
              display: none !important;
            }
            .print-table-logo-row th {
              background: transparent !important;
              color: black !important;
              border: none !important;
              padding: 4px 0 6px;
            }
            .print-table-logo {
              display: none !important;
            }
            .stat-badge {
              background: white !important;
              color: black !important;
              border: 1px solid black !important;
              padding: 2px 6px;
              font-size: 13px;
              font-weight: bold;
            }
            .amount {
              font-weight: bold;
              color: black !important;
              font-size: 14px;
            }
            .total-amount {
              font-weight: bold;
              color: black !important;
              font-size: 15px;
            }
            .signature-section {
              margin-top: 16px;
              padding-top: 8px;
              page-break-inside: avoid;
              page-break-after: auto;
              break-after: auto;
            }
            .signature-title {
              font-weight: bold;
              color: black !important;
              margin-bottom: 12px;
              font-size: 13px;
              text-align: center;
            }
            .signature-line {
              height: 50px;
              border-bottom: 1px solid black;
              margin-bottom: 3px;
              background: white !important;
            }
            .ml-2, .badge:not(.stat-badge) {
              display: none !important;
            }
            .orders-section {
              margin-bottom: 15px;
              /* Allow section to break across pages */
            }
            .section-header {
              page-break-after: avoid;
            }
            .company-info-section {
              page-break-inside: avoid;
            }
            .summary-section {
              page-break-inside: avoid;
            }
            * {
              transition: none !important;
              animation: none !important;
              transform: none !important;
            }
          }
        </style>
      `

      // Add the print styles to the document head
      document.head.insertAdjacentHTML('beforeend', printCSS)

      // Trigger print dialog
      window.print()

      // Remove the print styles after printing
      setTimeout(() => {
        const printStyleElement = document.getElementById('print-styles-bw')
        if (printStyleElement) {
          printStyleElement.remove()
        }
      }, 1000)
    }

    return {
      invoiceData,
      paymentDetails,
      printInvoice,
      printInvoiceBW,
      sommeHT,
      tax,
      total,
      sommeFrais,
    }
  },
}
</script>

<style lang="scss" scoped>
// Brand Colors - Team Parcel Express (Teal/Navy Theme)
$brand-primary: #0A2634;  // Dark teal/navy from branding
$brand-secondary: #1D1D1B; // Dark charcoal
$brand-light: #e8f4f8;    // Light teal
$brand-accent: #1a4a5e;   // Lighter teal accent
$brand-dark: #061a22;     // Very dark teal

.invoice-preview-wrapper {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  min-height: 100vh;
  font-size: 16px; /* Increased base font size */
}

.print-table-logo-row {
  display: none;
}

.print-table-logo {
  height: 18px;
  width: auto;
}

@media print {
  html, body {
    margin: 0 !important;
    padding: 0 !important;
  }
}

.top-action-bar {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-left: 4px solid rgba(17, 64, 146, 0.75);
}

.page-title {
  color: #1f1f1f;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.action-buttons-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: 1rem;
  }
}

.action-btn-top {
  border-radius: 8px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
  border-color: rgba(17, 64, 146, 0.65);
  color: #114092;

  &:hover {
    background-color: rgba(10, 38, 52, 0.1);
    border-color: rgba(17, 64, 146, 0.75);
    color: #114092;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10, 38, 52, 0.2);
  }

  // Dropdown specific styles
  &.dropdown .btn {
    border-color: rgba(17, 64, 146, 0.65);
    color: #114092;
    background-color: transparent;

    &:hover, &:focus {
      background-color: rgba(17, 64, 146, 0.08) !important;
      border-color: rgba(17, 64, 146, 0.75) !important;
      color: #114092 !important;
      box-shadow: 0 4px 12px rgba(10, 38, 52, 0.2) !important;
    }

    &:active, &.active, &.show {
      background-color: rgba(17, 64, 146, 0.12) !important;
      border-color: rgba(17, 64, 146, 0.75) !important;
      color: #114092 !important;
      box-shadow: 0 4px 12px rgba(10, 38, 52, 0.25) !important;
    }
  }

  .dropdown-menu {
    border-radius: 8px;
    border: 1px solid rgba(10, 38, 52, 0.2);
    box-shadow: 0 8px 25px rgba(10, 38, 52, 0.15);
    margin-top: 0.5rem;

    .dropdown-item {
      padding: 0.75rem 1rem;
      font-weight: 500;
      color: $brand-secondary;
      transition: all 0.2s ease;
      border-radius: 6px;
      margin: 0.25rem;

      &:hover, &:focus {
        background-color: rgba(10, 38, 52, 0.08) !important;
        color: $brand-primary !important;
        transform: translateX(2px);
      }

      .feather-icon {
        color: $brand-primary;
        transition: color 0.2s ease;
      }
    }
  }
}

.quick-stats {
  gap: 0.5rem;
  align-items: center;

  @media (max-width: 768px) {
    margin-left: 0 !important;
    margin-top: 0.5rem;
  }
}

.stat-item-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  padding: 0.55rem 0.85rem;
  margin-left: 0;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.06);

  svg {
    margin-right: 0.35rem;
  }

  &.success {
    background: rgba(72, 187, 120, 0.12);
    color: #0f8a63;
  }

  &.warning {
    background: rgba(237, 137, 54, 0.14);
    color: #b45b00;
  }

  &.primary {
    background: rgba(17, 64, 146, 0.12);
    color: #114092;
  }
}

.modern-alert {
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 12px rgba(10, 38, 52, 0.15);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
}

.invoice-content {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.invoice-card {
  border-radius: 16px;
  border: none;
  overflow: hidden;
  background: #fff;
}

.invoice-header {
  padding: 2rem;
  background: linear-gradient(135deg, rgba(17, 64, 146, 0.10) 0%, rgba(17, 64, 146, 0.10) 100%);
  color: #121212;
  margin: -1.25rem -1.25rem 2rem -1.25rem;
}

.invoice-header .invoice-title {
  color: #121212;
}

.invoice-header .invoice-number {
  color: #114092;
}

.invoice-header .invoice-date {
  color: #1f1f1f;
}

.company-logo {
  .logo-img {
    max-width: 200px;
    height: auto;
    background: transparent;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
  }
}

// Remove the @media print section since we're using dynamic CSS injection
// Keep all the screen styles but remove print styles

.top-action-bar {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #114092;
}

.page-title {
  color: #1f1f1f;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.action-buttons-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: 1rem;
  }
}

.action-btn-top {
  border-radius: 8px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
  border-color: $brand-primary;
  color: $brand-primary;

  &:hover {
    background-color: rgba(10, 38, 52, 0.1);
    border-color: $brand-primary;
    color: $brand-primary;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10, 38, 52, 0.2);
  }

  // Dropdown specific styles
  &.dropdown .btn {
    border-color: $brand-primary;
    color: $brand-primary;
    background-color: transparent;

    &:hover, &:focus {
      background-color: rgba(10, 38, 52, 0.1) !important;
      border-color: $brand-primary !important;
      color: $brand-primary !important;
      box-shadow: 0 4px 12px rgba(10, 38, 52, 0.2) !important;
    }

    &:active, &.active, &.show {
      background-color: rgba(10, 38, 52, 0.15) !important;
      border-color: $brand-primary !important;
      color: $brand-primary !important;
      box-shadow: 0 4px 12px rgba(10, 38, 52, 0.25) !important;
    }
  }

  .dropdown-menu {
    border-radius: 8px;
    border: 1px solid rgba(10, 38, 52, 0.2);
    box-shadow: 0 8px 25px rgba(10, 38, 52, 0.15);
    margin-top: 0.5rem;

    .dropdown-item {
      padding: 0.75rem 1rem;
      font-weight: 500;
      color: $brand-secondary;
      transition: all 0.2s ease;
      border-radius: 6px;
      margin: 0.25rem;

      &:hover, &:focus {
        background-color: rgba(10, 38, 52, 0.08) !important;
        color: $brand-primary !important;
        transform: translateX(2px);
      }

      .feather-icon {
        color: $brand-primary;
        transition: color 0.2s ease;
      }
    }
  }
}

.quick-stats {
  gap: 0.5rem;
  align-items: center;

  @media (max-width: 768px) {
    margin-left: 0 !important;
    margin-top: 0.5rem;
  }
}

.stat-item-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  padding: 0.5rem 0.75rem;
  margin-left: 0;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 800;
  line-height: 1;

  span {
    font-weight: 800;
  }

  svg {
    margin-right: 0.35rem;
  }

  &.success {
    background: rgba(72, 187, 120, 0.1);
    color: #48bb78;
  }

  &.warning {
    background: rgba(237, 137, 54, 0.1);
    color: #ed8936;
  }

  &.primary {
    background: rgba(10, 38, 52, 0.1);
    color: $brand-primary;
  }
}

.modern-alert {
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 12px rgba(10, 38, 52, 0.15);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
}

.invoice-content {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.invoice-card {
  border-radius: 16px;
  border: none;
  overflow: hidden;
  background: #fff;
}

.invoice-header {
  padding: 2rem;
  background: linear-gradient(135deg, rgba(17, 64, 146, 0.10) 0%, rgba(17, 64, 146, 0.10) 100%);
  color: #121212;
  margin: -1.25rem -1.25rem 2rem -1.25rem;
}

.company-logo {
  .logo-img {
    max-width: 200px;
    height: auto;
    background: transparent;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
  }
}

.invoice-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.invoice-number {
  color: #114092;
}

.invoice-date {
  font-size: 1rem;
  opacity: 0.9;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.company-info-section {
  margin-bottom: 2rem;
}

.info-card {
  background: #f7fafc;
  border-radius: 12px;
  padding: 1.5rem;
  height: 100%;
  border-left: 4px solid #114092;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
}

.supplier-card {
  border-left-color: #114092;
}

.info-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: $brand-secondary;
  font-weight: 600;
}

.info-content {
  .info-item {
    margin-bottom: 0.75rem;
    font-size: 1.05rem; /* Increased from default */

    &:last-child {
      margin-bottom: 0;
    }
  }

  .info-subtitle {
    display: block;
    font-size: 0.95rem; /* Increased from default */
    color: #718096;
    font-style: italic;
  }

  .info-label {
    font-weight: 600;
    color: $brand-secondary;
    display: inline-block;
    min-width: 120px;
    font-size: 1.05rem; /* Increased font size */
  }
}

.summary-section, .orders-section {
  margin-bottom: 2rem;
}

.section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: $brand-secondary;
  margin-bottom: 1rem;
  font-size: 1.25rem; /* Increased font size */
}

.section-header {
  margin-bottom: 1rem;
  font-size: 1.15rem; /* Increased font size */
}

.summary-table-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.modern-table {
  width: 100%;
  margin-bottom: 0;

  thead {
    background: linear-gradient(135deg, #000000 0%, #111111 100%);

    th {
      padding: 1rem;
      color: #ffffff;
      font-weight: 600;
      border: none;
      font-size: 0.9rem;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e2e8f0;

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 1rem;
      text-align: center;
      vertical-align: middle;
      border: none;
      font-size: 1.05rem; /* Increased font size */
    }
  }
}

.stat-badge {
  font-size: 1.1rem; /* Increased font size */
  padding: 0.5rem 0.75rem;
  font-weight: 600;
}

.amount {
  font-weight: 600;
  color: $brand-secondary;
  white-space: nowrap;
  font-size: 1.05rem; /* Increased font size */

  &.total-amount {
    color: $brand-primary;
    font-weight: 700;
    font-size: 1.15rem; /* Larger for emphasis */
  }

  &.highlight-fee {
    color: #f59e0b;
    font-weight: 600;
    background: #fef3c7;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
  }
}

.summary-fee-breakdown {
  display: grid;
  gap: 0.35rem;
  min-width: 150px;
  text-align: left;
}

.summary-fee-line {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  color: #9a5b00;
  font-size: 0.95rem;
  line-height: 1.2;

  span {
    white-space: normal;
  }

  strong {
    white-space: nowrap;
  }
}

.table-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.modern-orders-table {
  margin-bottom: 0;

  thead th {
    background: #f7fafc;
    border-bottom: 2px solid #e2e8f0;
    font-weight: 600;
    color: $brand-secondary;
    padding: 1rem 0.75rem;
    font-size: 1.05rem; /* Increased font size */
  }

  tbody {
    tr {
      transition: all 0.2s ease;

      &:hover {
        background-color: #f7fafc;
      }
    }

    td {
      padding: 0.75rem;
      border-color: #edf2f7;
      font-size: 1.05rem; /* Increased font size */
    }
  }
}

.order-code {
  strong {
    color: $brand-secondary;
  }
}

.return-reasons {
  max-width: 350px;
  min-width: 280px;
}

.reason-item {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.reason-main {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem;
  border-left: 3px solid $brand-primary;
}

.reason-text {
  margin-bottom: 0.5rem;
}

.reason-title {
  display: block;
  color: #1a4a5e;
  font-weight: 700;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.reason-note {
  display: flex;
  align-items: flex-start;
  font-size: 0.8rem;
  color: #6c757d;
  font-style: italic;
  margin-top: 0.25rem;

  svg {
    margin-top: 0.125rem;
    flex-shrink: 0;
  }
}

.reason-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.reason-date {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #495057;

  &.scheduled {
    color: #007bff;
  }

  svg {
    flex-shrink: 0;
  }
}

.date-label {
  font-weight: 600;
  margin-right: 0.25rem;
}

.date-value {
  font-weight: 500;
}

.reason-options {
  margin-top: 0.5rem;
}

.options-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.option-badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}

.reason-divider {
  height: 1px;
  background: #dee2e6;
  margin: 0.75rem 0;
}

.no-reason {
  display: flex;
  align-items: center;
  font-style: italic;
  color: #6c757d;
  font-size: 0.875rem;
}

// Print styles for return reasons
@media print {
  .return-reasons {
    max-width: none;

    .reason-main {
      background: #f8f9fa !important;
      border-left: 3px solid #0A2634 !important;
      -webkit-print-color-adjust: exact;
    }

    .reason-title {
      color: #0A2634 !important;
      -webkit-print-color-adjust: exact;
    }
  }
}

// Responsive adjustments for return reasons
@media (max-width: 768px) {
  .return-reasons {
    max-width: 250px;
    min-width: 200px;
  }

  .reason-main {
    padding: 0.5rem;
  }

  .reason-title {
    font-size: 0.8rem;
  }

  .reason-meta {
    .reason-date {
      font-size: 0.7rem;
    }
  }

  .options-list {
    .option-badge {
      font-size: 0.65rem;
      padding: 0.2rem 0.4rem;
    }
  }
}
</style>
